const STORAGE_KEY = 'activeTab'

const FRAGMENT_PATTERN = /^#[\w-]+$/

const SUPPORTS_COLOR_MIX =
  typeof CSS !== 'undefined' &&
  CSS.supports('color', 'color-mix(in srgb, red 50%, blue)')

function tabColor(weight) {
  if (weight >= 1) return 'var(--text-active-color)'
  if (weight <= 0 || !SUPPORTS_COLOR_MIX) return 'var(--text-color)'
  const percent = Math.round(weight * 100)
  return `color-mix(in srgb, var(--text-active-color) ${percent}%, var(--text-color))`
}

function readSavedTab() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function saveTab(fragment) {
  try {
    localStorage.setItem(STORAGE_KEY, fragment)
  } catch {
  }
}

class NavigationDrawerTabs extends HTMLElement {
  #abortController = null
  #resizeObserver = null
  #scroller = null
  #nav = null
  #indicator = null
  #items = []
  #metrics = []
  #painting = false
  #scrollEndTimer

  connectedCallback() {
    const scroller = this.querySelector(':scope > section')
    const nav = this.querySelector(':scope nav')
    if (!(scroller instanceof HTMLElement) || !(nav instanceof HTMLElement)) {
      return
    }

    this.#scroller = scroller
    this.#nav = nav
    this.#indicator = this.querySelector(':scope .snap-indicator')
    this.#items = Array.from(nav.querySelectorAll(':scope a'))
    this.#metrics = []
    if (this.#items.length === 0) return

    this.#abortController = new AbortController()
    const { signal } = this.#abortController

    nav.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof HTMLAnchorElement) {
          this.#setActiveTab(event.target, true)
        }
      },
      { signal }
    )

    scroller.addEventListener(
      'scroll',
      () => {
        this.#schedulePaint()
        clearTimeout(this.#scrollEndTimer)
        this.#scrollEndTimer = setTimeout(() => this.#syncActiveTab(), 100)
      },
      { passive: true, signal }
    )

    document.addEventListener(
      'dialog:after-show',
      (event) => {
        if (!(event.target instanceof Node) || !event.target.contains(this)) {
          return
        }
        this.#restoreSavedTab()
        this.#measure()
        this.#schedulePaint()
      },
      { signal }
    )

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#measure()) this.#schedulePaint()
    })
    this.#resizeObserver.observe(this)

    if (document.readyState === 'complete') {
      this.#start()
    } else {
      window.addEventListener('load', () => this.#start(), {
        once: true,
        signal,
      })
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#abortController = null
    this.#resizeObserver?.disconnect()
    this.#resizeObserver = null
    clearTimeout(this.#scrollEndTimer)
  }

  /**
   * Impact moves the drawer to `document.body` while open and back afterwards,
   * which zeroes the scroller, so the resting tab never comes from geometry.
   */
  #start() {
    const hash = location.hash
    const fromHash = FRAGMENT_PATTERN.test(hash)
      ? this.#nav?.querySelector(`a[href="${hash}"]`)
      : null
    if (fromHash) {
      this.#setActiveTab(fromHash, true)
    } else {
      this.#restoreSavedTab()
    }
    this.#schedulePaint()
  }

  #measure() {
    if (!this.offsetParent) return false
    this.#metrics = this.#items.map((item) => ({
      left: item.offsetLeft,
      width: item.offsetWidth,
    }))
    return true
  }

  #position() {
    const page = this.#scroller?.clientWidth ?? 0
    if (!page) return 0
    const scrolled = (this.#scroller?.scrollLeft ?? 0) / page
    return Math.min(this.#items.length - 1, Math.max(0, scrolled))
  }

  #schedulePaint() {
    if (this.#painting) return
    this.#painting = true
    requestAnimationFrame(() => this.#paint())
  }

  #paint() {
    this.#painting = false
    if (this.#metrics.length !== this.#items.length && !this.#measure()) return

    const at = this.#position()
    const from = this.#metrics[Math.floor(at)]
    const to = this.#metrics[Math.ceil(at)]
    const ratio = at - Math.floor(at)
    if (from && to && this.#indicator) {
      const left = from.left + (to.left - from.left) * ratio
      const width = from.width + (to.width - from.width) * ratio
      this.#indicator.style.transform = `translateX(${left}px)`
      this.#indicator.style.width = `${width}px`
    }
    this.#items.forEach((item, index) => {
      item.style.color = tabColor(Math.max(0, 1 - Math.abs(index - at)))
    })
  }

  #setActiveTab(item, scroll) {
    const current = this.#nav?.querySelector(':scope a[active]')
    if (current && current !== item) {
      current.removeAttribute('active')
      current.removeAttribute('aria-current')
    }
    item.setAttribute('active', '')
    item.setAttribute('aria-current', 'true')

    const fragment = item.getAttribute('href') ?? ''
    saveTab(fragment)
    if (!scroll || !FRAGMENT_PATTERN.test(fragment)) return
    const panel = this.querySelector(fragment)
    if (panel instanceof HTMLElement) {
      this.#scroller?.scrollTo({ left: panel.offsetLeft, behavior: 'instant' })
    }
  }

  #syncActiveTab() {
    const page = this.#scroller?.clientWidth ?? 0
    if (!page || !this.closest('[open]')) return
    const index = Math.round((this.#scroller?.scrollLeft ?? 0) / page)
    const item = this.#items[index]
    if (item) this.#setActiveTab(item, false)
  }

  #restoreSavedTab() {
    const saved = readSavedTab()
    if (!saved || !FRAGMENT_PATTERN.test(saved)) return
    const restored = this.#nav?.querySelector(`a[href="${saved}"]`)
    if (restored) this.#setActiveTab(restored, true)
  }
}

if (!customElements.get('snap-tabs')) {
  customElements.define('snap-tabs', NavigationDrawerTabs)
}
